import { Fragment } from "react";

import styled from "styled-components/macro";

import { useTierPricingContext } from "Payments/TierPricing/TierPricingProvider";
import {
  useAddTier,
  useMaxTierCost,
  useNonMaxTierData,
  useSetTier,
} from "Payments/TierPricing/tierPricingHooks";
import { AnimatePresence, motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import Tier from "./Tier/Tier";

const Tiers = () => {
  const { setTierData, tierData, setErrorMessage } = useTierPricingContext();
  const setTier = useSetTier();
  const nonMaxTierData = useNonMaxTierData();

  const [maxTierCost] = useMaxTierCost();

  const deleteTier = ({ id }) => {
    const newTierData = tierData.filter((element) => element.id !== id);
    setErrorMessage("");
    setTierData(newTierData);
  };

  if (nonMaxTierData.every((item) => !item.id)) return null;

  return (
    <>
      <AddTierButtonAndDivider
        addAfterID={0}
        numberOfSlots={0}
        numberOfSlotsAfter={nonMaxTierData[0].numberOfSlots}
        price={0}
        priceAfter={nonMaxTierData[0].amount}
        numberOfTiers={nonMaxTierData.length}
        hideLines={true}
      />

      {nonMaxTierData.map(
        (
          {
            amount,
            numberOfSlots,
            id,
            isNew,
            shouldFlashSlots,
            shouldFlashAmount,
          },
          idx
        ) => {
          return (
            <Fragment key={id}>
              <Tier
                isNew={isNew}
                amount={amount}
                numberOfSlots={numberOfSlots}
                setData={({
                  amount: newAmount,
                  numberOfSlots: newNumberOfSlots,
                }) => setTier({ newAmount, newNumberOfSlots, id })}
                shouldFlashSlots={shouldFlashSlots}
                shouldFlashAmount={shouldFlashAmount}
                deleteData={() => deleteTier({ id })}
                tierDataBefore={nonMaxTierData[idx - 1]}
                tierDataAfter={nonMaxTierData[idx + 1]}
                minSlots={idx + 1}
                numberOfNonMaxTiers={nonMaxTierData.length}
              />
              <AddTierButtonAndDivider
                addAfterID={id}
                numberOfSlots={numberOfSlots}
                numberOfSlotsAfter={nonMaxTierData[idx + 1]?.numberOfSlots}
                price={amount}
                priceAfter={nonMaxTierData[idx + 1]?.amount || maxTierCost}
                numberOfTiers={nonMaxTierData.length}
              />
            </Fragment>
          );
        }
      )}
    </>
  );
};

export default Tiers;

function AddTierButtonAndDivider({
  addAfterID,
  numberOfSlots,
  numberOfSlotsAfter,
  price,
  priceAfter,
  numberOfTiers,
  hideLines,
}) {
  const addTier = useAddTier();

  const calculateAndAddTier = () => {
    let newPrice = price + 2;

    if (priceAfter - price > 5) newPrice = Math.floor((priceAfter - price) / 2);

    addTier(numberOfSlots + 1, newPrice);
  };

  return (
    <DividerWrapper data-cy="Tier_Divider">
      <DividerLine style={hideLines && { opacity: 0 }} />
      <AnimatePresence>
        {numberOfTiers < 4 &&
          (numberOfSlotsAfter - numberOfSlots > 1 || !numberOfSlotsAfter) && (
            <AddTierButtonHolder
              initial={{ scale: 0.6, opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto", scale: 1 }}
              exit={{
                scale: 0.6,
                opacity: 0,
                width: 0,
                transition: { type: "tween", duration: 0.2, ease: "easeOut" },
              }}
            >
              <AddTierButtonPadding>
                <AddTierButton
                  onClick={calculateAndAddTier}
                  whileHover={{
                    backgroundColor: colors.fnBlue,
                    outline: `1px solid ${colors.fnBlue}`,
                    color: "#FFF",
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FnrIcon fad fa-layer-group />
                  Add a Tier
                </AddTierButton>
              </AddTierButtonPadding>
            </AddTierButtonHolder>
          )}
      </AnimatePresence>
      <DividerLine style={hideLines && { opacity: 0 }} />
    </DividerWrapper>
  );
}

const DividerWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
`;
const DividerLine = styled.div`
  height: 1px;
  background: ${colors.fnBorderGray};
  flex-grow: 1;
`;
const AddTierButtonHolder = styled(motion.div)`
  flex-shrink: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const AddTierButtonPadding = styled.div`
  padding: 0 4px;
`;

const AddTierButton = styled(motion.button)`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  color: ${colors.fnDkGray};
  outline: 1px solid ${colors.fnBorderGray};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 24px;
  background-color: transparent;
  border: none;
  transition: 0.3s all;

  &:focus {
    background-color: ${colors.fnBlue} !important;
    border: 1px solid ${colors.fnBlue} !important;
    color: white !important;
  }
`;
